/* eslint-disable react-hooks/exhaustive-deps, react/jsx-props-no-spreading */
import { useEffect, createContext, useCallback, useMemo, useState } from "react";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { trackEvents } from "utils/trackingUtils";
import { Chakra } from "@airhelp/react-ui-components";

const { Modal: ChakraModal, ModalContent, ModalOverlay, useDisclosure, useBreakpointValue } = Chakra;

export const ModalContext = createContext({});

export const ModalContextProvider = ({ children, gtmEvent, mixpanelEvent }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalContent, setModalContent] = useState(null);
  const [size, setModalSize] = useState();
  const router = useRouter();

  const handleHashChange = () => {
    onClose();
    trackEvents({ gtmEvent, mixpanelEvent });
    window.removeEventListener("hashchange", handleHashChange);
  };

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const openModal = useCallback(
    (content, modalSize = "sm") => {
      if (content) {
        onOpen();
        setModalContent(content);
        setModalSize(modalSize);
        const pathToPush = router.asPath.includes("#") ? `${router.asPath}modal` : `${router.asPath}#modal`;
        router.push(pathToPush, null, { shallow: true });
        window.addEventListener("hashchange", handleHashChange);
      }
    },
    [onOpen, router]
  );

  const closeModal = useCallback(() => {
    onClose();
    router.push(`${router.asPath}#`, null, { shallow: true });
    trackEvents({ gtmEvent, mixpanelEvent });
    window.removeEventListener("hashchange", handleHashChange);
  }, [onClose, gtmEvent, mixpanelEvent]);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
      onClose,
    }),
    [openModal, closeModal]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      <ChakraModal
        isOpen={isOpen}
        onClose={closeModal}
        size={useBreakpointValue({ base: "full", md: size })}
        isCentered={false}
      >
        <ModalOverlay bg="rgba(69, 84, 115, 0.7)" />
        <ModalContent
          overflow="auto"
          minHeight={{ base: "100vh", md: "initial" }}
          mt={{ base: 0, md: size === "full" ? 4 : 12 }}
          {...(size === "full" ? { mb: 0 } : null)}
          {...(size === "xl" ? { maxWidth: "755px" } : null)}
        >
          {modalContent}
        </ModalContent>
      </ChakraModal>
    </ModalContext.Provider>
  );
};

ModalContextProvider.propTypes = {
  children: PropTypes.node,
  gtmEvent: PropTypes.arrayOf(PropTypes.string),
  mixpanelEvent: PropTypes.string,
};

ModalContextProvider.defaultProps = {
  children: null,
  gtmEvent: null,
  mixpanelEvent: null,
};
